import { useMemo, useState } from "react";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import type { AthleteRelationship, Player } from "@/lib/types";
import { useAthleteDetail } from "@/features/athletes/hooks/useAthletes";
import { CoachModal } from "./CoachModal";
import { useCoachUsers, type RosterMutations } from "../hooks/useRoster";
import { TkticSelect } from "@/components/ui/TkticSelect";

/**
 * Link-account modal — Coach > Roster.
 *
 * Ports the legacy `openCoachLinkModal` (`js/coaching/roster.js`).
 * Since #908 (D4) the link is made to the ATHLETE behind the roster row,
 * not to the row: the service projects it onto every assignment the
 * athlete holds, so a guardian linked here keeps following the child
 * across teams and seasons. The existing links are read from the
 * athlete detail (`links` is served to this team's staff) and revoked
 * at the athlete level too.
 *
 * `useCoachUsers` (gated open here) loads the team's linkable users;
 * the picker excludes accounts already linked to THIS athlete so a
 * coach can't double-link the same user. The relationship enum mirrors
 * `AthleteUserLink.relationship` — `guardian` / `self` / `family`.
 */

const RELATIONSHIP_OPTIONS: ReadonlyArray<{
  value: AthleteRelationship;
  label: string;
}> = [
  { value: "guardian", label: "Parent / guardian" },
  { value: "self", label: "Player" },
  { value: "family", label: "Family" },
];

function relationshipLabel(value: AthleteRelationship) {
  return RELATIONSHIP_OPTIONS.find((option) => option.value === value)?.label ?? "Family";
}

export type LinkAccountModalProps = {
  player: Player;
  mutations: RosterMutations;
  onClose: () => void;
  onSaved?: () => void;
};

export function LinkAccountModal({
  player,
  mutations,
  onClose,
  onSaved,
}: LinkAccountModalProps) {
  const toast = useToast();
  const athleteId = player.athlete_id ?? null;
  // Fetch the team's linkable users only while this modal is open.
  const usersQuery = useCoachUsers(true);
  const detailQuery = useAthleteDetail(athleteId);
  const [userId, setUserId] = useState("");
  const [relationship, setRelationship] =
    useState<AthleteRelationship>("guardian");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  // #908: the backend refuses (403) a link or unlink unless the viewer is a
  // club admin or manages EVERY team the athlete currently plays for.
  const linksBlocked = detailQuery.data?.can_manage_links === false;
  const canEditLinks = detailQuery.data?.can_manage_links === true;

  const links = useMemo(
    () => (detailQuery.data?.links ?? []).filter((link) => !link.revoked_at),
    [detailQuery.data],
  );

  // Exclude accounts already linked to this athlete.
  const linkedUserIds = useMemo(
    () => new Set(links.map((link) => link.user_id)),
    [links],
  );
  const availableUsers = useMemo(
    () =>
      (usersQuery.data?.users ?? []).filter((u) => !linkedUserIds.has(u.id)),
    [usersQuery.data, linkedUserIds],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!athleteId) {
      setError("This roster row has no athlete record yet.");
      return;
    }
    if (!userId) {
      setError("Pick an account to link.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await mutations.linkAthleteAccount(athleteId, {
        user_id: userId,
        relationship,
      });
      toast.success("Account linked");
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : "Could not link the account.",
      );
      toast.error("Couldn't link account", { description: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (linkId: number) => {
    if (!athleteId) return;
    setError(null);
    setRemovingId(linkId);
    try {
      await mutations.revokeAthleteLink(athleteId, linkId);
      toast.success("Account link removed");
      onSaved?.();
    } catch (err) {
      setError(errorMessage(err));
      toast.error("Couldn't remove account link", {
        description: errorMessage(err),
      });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <CoachModal
      title={`Manage linked accounts · ${player.display_name}`}
      onClose={onClose}
      data-testid="roster-link-modal"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="roster-link-account-form"
            disabled={
              submitting ||
              !athleteId ||
              !canEditLinks ||
              availableUsers.length === 0
            }
            data-testid="link-modal-submit"
          >
            {submitting ? "Linking…" : "Link account"}
          </Button>
        </div>
      }
    >
      <form
        id="roster-link-account-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit}
      >
        <section aria-labelledby="linked-accounts-heading">
          <h3
            id="linked-accounts-heading"
            className="font-display text-base text-ink"
          >
            Linked accounts
          </h3>
          <p className="mt-1 text-sm text-ink-muted" data-testid="link-modal-athlete-note">
            Links belong to the athlete and follow them across teams and
            seasons — a linked account sees every team the athlete is
            rostered on.
          </p>
          {linksBlocked ? (
            <p
              className="mt-2 text-sm text-ink-muted"
              data-testid="link-modal-blocked"
            >
              This athlete also plays for another team you don&rsquo;t
              manage, so a club admin must link or unlink their accounts.
            </p>
          ) : null}
          {detailQuery.isLoading ? (
            <p className="mt-2 rounded border border-rule bg-elevated px-3 py-3 text-sm text-ink-muted font-mono">
              Loading linked accounts…
            </p>
          ) : links.length > 0 ? (
            <ul
              className="mt-2 divide-y divide-rule rounded border border-rule bg-elevated"
              data-testid="link-modal-links"
            >
              {links.map((link) => (
                <li
                  key={link.id}
                  className="flex items-center justify-between gap-3 px-3 py-2.5"
                  data-testid="link-modal-link"
                  data-user-id={link.user_id}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-ink">
                      {link.display_name || link.username || link.user_id}
                    </p>
                    {link.username ? (
                      <p className="truncate text-xs text-ink-muted">
                        @{link.username}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full bg-field px-2 py-1 text-xs text-ink-muted">
                      {relationshipLabel(link.relationship)}
                    </span>
                    <button
                      type="button"
                      onClick={() => void handleRemove(link.id)}
                      disabled={removingId !== null || !canEditLinks}
                      data-testid="link-modal-revoke"
                      className="rounded px-2 py-1 text-sm text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
                    >
                      {removingId === link.id ? "Removing…" : "Remove"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 rounded border border-rule bg-elevated px-3 py-3 text-sm text-ink-muted">
              {detailQuery.isError
                ? errorMessage(detailQuery.error)
                : "No accounts are linked yet."}
            </p>
          )}
        </section>

        <div className="border-t border-rule pt-4">
          <h3 className="font-display text-base text-ink">
            Add another account
          </h3>
          <p className="mt-1 text-sm text-ink-muted">
            Choose from verified accounts with access to this club.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Account
          </span>
          <TkticSelect
            value={userId}
            onChange={(next) => setUserId(next)}
            data-testid="link-modal-user"
            disabled={usersQuery.isLoading}
            ariaLabel="Account"
            options={[
              {
                value: "",
                label: usersQuery.isLoading
                  ? "Loading accounts…"
                  : availableUsers.length === 0
                    ? "No linkable accounts"
                    : "Select an account…",
              },
              ...availableUsers.map((u) => ({
                value: u.id,
                label: `${u.display_name} · @${u.username}`,
              })),
            ]}
          />
        </label>

        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Relationship
          </span>
          <TkticSelect
            value={relationship}
            onChange={(next) => setRelationship(next as AthleteRelationship)}
            data-testid="link-modal-relationship"
            ariaLabel="Relationship"
            options={RELATIONSHIP_OPTIONS.map((r) => ({
              value: r.value,
              label: r.label,
            }))}
          />
        </label>
        </div>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="link-modal-error"
          >
            {error}
          </p>
        ) : null}
      </form>
    </CoachModal>
  );
}
